import React from 'react';
import { motion } from 'framer-motion';
import EnhancedSmartGrid from '../../components/SmartGrid/EnhancedSmartGrid';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Brain, Zap, BarChart3, Settings, Users, GitBranch, Database } from 'lucide-react';

const EnhancedSmartGridPage: React.FC = () => {
  const handleDataChange = (workbook: any) => {
    console.log('Enhanced SmartGrid data changed:', workbook);
  };

  const handleExport = (format: string) => {
    console.log('Exporting Enhanced SmartGrid as:', format);
  };

  const handleShare = () => {
    console.log('Sharing Enhanced SmartGrid');
  };

  const handleRunPlaybook = () => {
    console.log('Running Enhanced SmartGrid playbook');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                <Brain className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                Enhanced SmartGrid
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                AI-powered spreadsheet with intelligent analysis, automation, and optimization
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Settings className="h-4 w-4" />
                Settings
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Users className="h-4 w-4" />
                Collaborate
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <GitBranch className="h-4 w-4" />
                Version History
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Feature Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <Card className="p-6 border-l-4 border-blue-500">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Brain className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">AI Analysis</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Intelligent data insights and anomaly detection
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6 border-l-4 border-green-500">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <Zap className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Automation</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Smart workflows and data processing
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6 border-l-4 border-purple-500">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <BarChart3 className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Analytics</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Advanced charts and data visualization
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6 border-l-4 border-orange-500">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                <Database className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Integration</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Connect with external data sources
                </p>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Enhanced SmartGrid Component */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <EnhancedSmartGrid
            entity="enhanced-demo"
            workbookName="demo-workbook"
            enableAI={true}
            enableCollaboration={true}
            onDataChange={handleDataChange}
            onExport={handleExport}
            onShare={handleShare}
            onRunPlaybook={handleRunPlaybook}
            className="mb-8"
          />
        </motion.div>

        {/* Footer Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-8"
        >
          <Card className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Enhanced SmartGrid Features
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-1">AI Capabilities</h4>
                  <ul className="space-y-1">
                    <li>• Anomaly Detection</li>
                    <li>• Trend Analysis</li>
                    <li>• Correlation Discovery</li>
                    <li>• Intelligent Suggestions</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-1">Automation</h4>
                  <ul className="space-y-1">
                    <li>• Workflow Generation</li>
                    <li>• Data Validation</li>
                    <li>• Conditional Formatting</li>
                    <li>• Data Cleaning</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-1">Advanced Features</h4>
                  <ul className="space-y-1">
                    <li>• Multi-sheet Workbooks</li>
                    <li>• Natural Language Queries</li>
                    <li>• Real-time Collaboration</li>
                    <li>• Performance Optimization</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default EnhancedSmartGridPage;